'use client'
import { useSyncExternalStore } from 'react';

/**
 * Custom hook that provides a slice of the store state based on a selector function.
 *
 * @template TState - The type of the store state.
 * @template StateSlice - The type of the selected slice of the store state.
 * @param {StoreApi<TState>} api - The store API object.
 * @param {(state: TState) => StateSlice} selector - The selector function to extract the desired store slice.
 * @returns {StateSlice} - The selected slice of the store state.
 *
 * @example
 * const store = createStore(() => ({
 *   count: 0,
 * }))
 *
 * const count = useStoreWithSelector(store, ({ count }) => count);
 */
const useStoreWithSelector = (api, selector) => {
    const getSnapshot = () => selector(api.getState());
    const getServerSnapshot = () => selector(api.getInitialState());
    const slice = useSyncExternalStore(api.subscribe, getSnapshot, getServerSnapshot);
    return slice;
};

export { useStoreWithSelector };
